import * as React from "react"
import styled from "styled-components"
import { navigate } from "gatsby"
import PropTypes from "prop-types"
import { verifyEmail, verifyPhone } from "./verification"

const Form = styled.form`
    background-color: #222a3f;
    padding: 20px;
    color: #fff;
    flex: 1 1 40%;
    min-width: 40%;
    max-width: 40%;
    position: relative;
    z-index: 1;

    @media (max-width: 1000px) {
        flex: 1 1 100%;
        min-width: 100%;
        max-width: 100%;
        width: 100%;
    }
`

const FlexWrap = styled.div`
    display: flex;
    justify-content: space-between;
`

const LabelGroup = styled.div`
    position: relative;
    width: 100%;
    margin-bottom: 30px;
    padding-top: 5px;

    &:focus-within {
        &::after {
            content: "";
            display: block;
            background: #D4397A;
            height: 3px;
            width: 100%;
            position: absolute;
            bottom: -1px;
        }
    }

    &:has(textarea):focus-within:after {
        bottom: 3px;
    }
`

// const CheckBoxGroup = styled.div`
//     display: flex;
//     gap: 8px;
//     align-items: flex-start;
//     margin-bottom: 30px;

//     input {
//         position: relative;
//         top: 0.2em;
//         flex: 1;
//         width: 16px;
//         heigh: 16px;
//         min-width: 16px;
//         min-heigh: 16px;
//     }

//     label {
//         word-wrap: break-word;

//         @media (max-width: 512px) {
//             font-size: 1rem;
//         }
//     }
// `

const Split50 = styled.div`
    flex: 1 1 48%;
    max-width: 48%;
    width: 100%;
`

const Input = styled.input`
    width: 100%;
    border: none;
    border-bottom: 1px solid #d6e1ef;
    padding: 5px;
    background: transparent;
    color: #fff;
    outline: none;

    &::placeholder {
        color: #fff;
        opacity: 1;
    }

    &:focus {
        border-bottom: 1px solid #D4397A;

        &::after {
            content: "";
            display: block;
            background: #D4397A;
            height: 2px;
            width: 100%;
            position: absolute;
            bottom: -1px;
        }
    }

    &:not(input:-webkit-autofill)::-webkit-contacts-auto-fill-button {
        background-color: #fff;
    }
`

const Select = styled.div`
    width: 100%;
    border: none;
    border-bottom: 1px solid #d6e1ef;
    padding: 5px;
    border-radius: 0;
    appearance: none;
    background-color: transparent;
    margin: 0;
    font-family: inherit;
    font-size: inherit;
    cursor: inherit;
    line-height: inherit;
    display: grid;
    grid-template-areas: "select";
    align-items: center;

    &::focus-within {
        border-bottom: 1px solid #D4397A;

        &::after {
            content: "";
            display: block;
            background: #D4397A;
            height: 2px;
            width: 100%;
            position: absolute;
            bottom: -1px;
        }
    }

    select {
        appearance: none;
        // Additional resets for further consistency
        background-color: transparent;
        border: none;
        padding: 0 1em 0 0;
        margin: 0;
        width: 100%;
        font-family: inherit;
        font-size: inherit;
        cursor: inherit;
        line-height: inherit;
        grid-area: select;
        outline: none;
        color: #fff;
        outline: none;
    }

    &::after {
        content: "";
        width: 0.8em;
        height: 0.5em;
        background-color: #fff;
        clip-path: polygon(100% 0%, 0 0%, 50% 100%);
        grid-area: select;
        justify-self: end;
    }
`

const Label = styled.label`
    position: absolute;
    pointer-events: none;
    left: 4px;
    top: 13px;
    opacity: 0;
    transition: all 300ms;

    select:valid ~ &,
    textarea:not(:placeholder-shown) + &,
    input:not(:placeholder-shown) + & {
        pointer-events: all;
        font-weight: 700;
        font-size: 16px;
        top: -15px;
        z-index: 2;
        opacity: 1;
    }

    textarea:not(:placeholder-shown) + & {
        top: -12px;
    }
`

const TextArea = styled.textarea`
    width: 100%;
    height: 100px;
    background-color: transparent;
    color: #fff;
    outline: none;

    &::placeholder {
        color: #fff;
        opacity: 1;
    }

    &:focus {
        border-color: #D4397A;
    }
`

// const Link = styled.a`
//     color: #fff;
// `

const Button = styled.button`
    border-radius: 0;
    font-weight: 800;
    line-height: 1;
    padding: 10px;
    width: 100%;
    text-align: center;
`

const ErrorMessage = styled.p`
    color: #FF6868;
    display: none;
    margin-bottom: 0;

    [aria-invalid="true"] ~ & {
        display: inline-block;
        max-width: 100%;
    }
`

const FormErrorMessage = styled.p`
    color: #FF6868;
    display: none;

    [data-error="true"] & {
        display: inline-block;
        max-width: 100%;
    }
`

const FormRequiredFieldMessage = styled.p`
    color: #FF6868;
    display: none;

    [data-required="true"] & {
        display: inline-block;
        max-width: 100%;
    }
`

const validateEmail = (val) => {
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(val);
}
const emailBlur = event => {
    const el = event.target;
    const value = el.value;
    
    if ( ! validateEmail(value) ) {
        el.setAttribute( 'aria-invalid', 'true' );
    } else {
        el.removeAttribute( 'aria-invalid' );
    }
}

const phoneRegex = /^(?:(\d{1,3})(?: |-|.|\+)?)?\(?(\d{3})(?:\)|\.|-| )? ?(\d{3})(?:-|\.| )?(\d{4})$/i;

const validatePhone = (val) => {
    return phoneRegex.test(val) || /\d{7}/.test(val.replace(/[^\d]/, '')); 
}
const phoneBlur = event => {
    const el = event.target;
    const value = el.value;
    
    if ( ! validatePhone(value) ) {
        el.setAttribute( 'aria-invalid', 'true' );
        return;
    } else {
        el.removeAttribute( 'aria-invalid' );
    }
}

const formError = (hasError=true, errorMessage = 'There was an error with the form.') => {
    const Form             = document.getElementById('discovery-form');
    const FormErrorMessage = document.getElementById('form-error');

    Form.setAttribute('data-error', hasError.toString() );

    if ( hasError ) {
        FormErrorMessage.innerText = errorMessage;
    } else {
        FormErrorMessage.innerText = '';
    }
}

const formRequired = (hasError=true) => {
    const Form             = document.getElementById('discovery-form');
    const FormErrorMessage = document.getElementById('form-required');

    Form.setAttribute('data-required', hasError.toString() );

    if ( hasError ) {
        FormErrorMessage.innerText = 'Please fill out all required fields.';
    } else {
        FormErrorMessage.innerText = '';
    }
}

const TopDocForm = ({ webHook, howDidYouHearAboutUsOptions, appointmentRegardingOptions }) => {
    const formSubmit = async e => {
        e.preventDefault();
        formError(false);
        formRequired(false);
    
        const data = new FormData(e.target);
        const values = {};
    
        for ( const pair of data.entries() ) {
            if ( 'extra' === pair[0] ) {
                continue;
            }
    
            if ( ! pair[1] ) {
                formRequired();
                return;
            }
    
            values[ pair[0] ] = pair[1];
        }
    
        const emailField = document.getElementById( 'email' );
    
        if ( ! validateEmail( emailField.value ) ) {
            formError();
            return;
        }
    
        const phoneField = document.getElementById( 'phone' );
    
        if ( ! validatePhone( phoneField.value ) ) {
            formError();
            return;
        }

        if ( ! await verifyEmail( emailField.value ) ) {
            formError(true, 'Please check your email address.');
            return;
        }

        if ( ! await verifyPhone( phoneField.value ) ) {
            formError(true, 'Please check your phone number.');
            return;
        }

        fetch(webHook, {
            method: 'POST',
            body: new FormData(e.target),
        }).then(response => {
            if (response.ok) {
                return response.json();
            }
            return Promise.reject(response);
        }).then( data => {
            if ( 'success' === data.status ) {
                navigate('/thank-you');
            }
        }).catch( error => {
            console.warn(error);
        });
    }

    return (
    <Form onSubmit={ formSubmit } id="discovery-form">
        <FlexWrap>
            <Split50>
                <LabelGroup>
                    <Input id="fname" name="fname" placeholder="First Name*" required />
                    <Label htmlFor="fname">First Name</Label>
                </LabelGroup>
            </Split50>
            <Split50>
                <LabelGroup>
                    <Input id="lname" name="lname" placeholder="Last Name*" required />
                    <Label htmlFor="lname">Last Name*</Label>
                </LabelGroup>
            </Split50>
        </FlexWrap>
        <LabelGroup>
            <Input id="email" name="email" placeholder="Email*" onBlur={emailBlur} required />
            <Label htmlFor="email">Email*</Label>
            <ErrorMessage>Please enter a valid email.</ErrorMessage>
        </LabelGroup>
        <LabelGroup>
            <Input id="phone" name="phone" placeholder="Phone*" type="tel" onBlur={phoneBlur} required />
            <Label htmlFor="phone">Phone*</Label>
            <ErrorMessage>Please enter a valid phone number.</ErrorMessage>
        </LabelGroup>
        <LabelGroup>
            <Select>
                <select id="regarding" name="regarding" required>
                    <option value="">Appointment Regarding*</option>
                    { appointmentRegardingOptions.map( (option, index) => {
                        return (
                            <option key={ index } value={ option.label }>{ option.label }</option>
                        )
                    } ) }
                </select>
                <span className="focus"></span>
                <Label htmlFor="regarding">Appointment Regarding*</Label>
            </Select>
        </LabelGroup>
        <LabelGroup>
            <Select>
                <select id="type" name="type" required>
                    <option value="">Appointment Type*</option>
                    <option value="In-office consultation">In-office consultation</option>
                    <option value="Virtual consultation">Virtual consultation</option>
                </select>
                <span className="focus"></span>
                <Label htmlFor="type">Appointment Type*</Label>
            </Select>
        </LabelGroup>
        <LabelGroup>
            <Select>
                <select id="how" name="how" required>
                    <option value="">How did you hear about us?*</option>
                    { howDidYouHearAboutUsOptions.map( (option, index) => {
                        return (
                            <option key={ index } value={ option.label }>{ option.label }</option>
                        )
                    } ) }
                </select>
                <span className="focus"></span>
                <Label htmlFor="how">How did you hear about us?*</Label>
            </Select>
        </LabelGroup>
        <LabelGroup>
            <TextArea id="extra" name="extra" placeholder="Anything else you would like us to know?" />
            <Label htmlFor="extra">Anything else you would like us to know?</Label>
        </LabelGroup>
        {/* <CheckBoxGroup>
            <input id="tos-pp" name="tos-pp" value="agree" type="checkbox" required />
            <label htmlFor="tos-pp">
                By submitting this form, you agree to receive transaction messages from The Office of Dr. Scheiner, M.D. Text and data rates may apply. Message frequency varies. Reply STOP to unsubscribe or HELP for help. To view our privacy policy, go to <Link href="https://adamscheinermd.com/privacy-policy/">https://adamscheinermd.com/privacy-policy/</Link>. To view our terms of service go to: <Link href="https://adamscheinermd.com/terms-of-service">https://adamscheinermd.com/terms-of-service.</Link>
            </label>
        </CheckBoxGroup> */}
        <Button type="submit">Submit</Button>
        <FormErrorMessage aria-live="assertive" aria-atomic="true" id="form-error" />
        <FormRequiredFieldMessage aria-live="assertive" aria-atomic="true" id="form-required" />
    </Form>
    )
}

TopDocForm.propTypes = {
    webHook: PropTypes.string,
    howDidYouHearAboutUsOptions: PropTypes.object,
    appointmentRegardingOptions: PropTypes.object,
}

export default TopDocForm;